import { useMemo } from 'react';
import {
  components,
  type OptionProps,
  type SingleValueProps,
} from 'react-select';

import { Box } from '@hivebrite/design-system/Box';
import { Image } from '@hivebrite/design-system/Image';
import { ReactSelectAsyncInput } from '@hivebrite/design-system/Inputs';
import { type ReactSelectInputProps } from '@hivebrite/design-system/Inputs';

import { makeFetchTerm } from 'hb-react/shared/components/DesignSystem/Inputs/ReactSelect/utils';

const noOptionsMessage = ({ inputValue }) =>
  inputValue
    ? __('User %{user} not found').i__({
        user: inputValue,
      })
    : __('Search for a user');

const CustomSingleValueComponent = (
  props: SingleValueProps<{
    data: {
      headline?: string;
      photo_url: string;
    };
  }>,
) => {
  const { data, children, options = [] } = props;

  const user = useMemo(
    () =>
      // @ts-expect-error: fix types
      data?.data || options.find((option) => option.value === data.value)?.data,
    [data, options],
  );

  return (
    <components.SingleValue {...props}>
      <Box
        alignItems="center"
        justifyContent="space-between"
        data-testid="react-select-option"
      >
        <Box justifyContent="center">
          {user?.photo_url && (
            <Box mr={3}>
              <Image src={user.photo_url} width={24} height={24} />
            </Box>
          )}
          <Box flexDirection="column" justifyContent="center">
            <Box font="body-regular-black" color="black">
              {children}
            </Box>
          </Box>
        </Box>
      </Box>
    </components.SingleValue>
  );
};

const CustomOptionComponent = (
  props: OptionProps<{
    data: {
      headline?: string;
      photo_url: string;
    };
    id: number;
  }>,
) => {
  const { data, isFocused, isSelected, children, options } = props;
  const { id, data: user } = data;

  return (
    <li
      aria-posinset={id}
      aria-selected={isSelected}
      aria-setsize={options.length}
      data-testid="react-select-option"
      role="option"
      tabIndex={-1}
    >
      <components.Option {...props}>
        <Box alignItems="center" justifyContent="space-between">
          <Box justifyContent="center">
            {user?.photo_url && (
              <Box mr={3}>
                <Image src={user.photo_url} width={32} height={32} />
              </Box>
            )}
            <Box flexDirection="column" justifyContent="center">
              <Box
                font="body-regular-black"
                color={isFocused ? 'white' : 'black'}
              >
                {children}
              </Box>
              {user?.headline && (
                <Box
                  font="metadata-semibold-asphalt"
                  color={isFocused ? 'white' : 'dove'}
                  mt={1}
                >
                  {user.headline}
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </components.Option>
    </li>
  );
};

const ReactSelectUsers = ({
  admin, // this key is used to know if the search is from the backoffice or not
  placeholder = __('User'),
  inputValue,
  searchType,
  defaultOptions = false,
  withAvatars = false,
  id,
  name,
  ...otherProps
}) => {
  const customComponents = useMemo(
    () =>
      withAvatars
        ? {
            Option: CustomOptionComponent,
            SingleValue: CustomSingleValueComponent,
          }
        : {},
    [withAvatars],
  ) as ReactSelectInputProps<
    OptionProps & { value: number | string },
    boolean,
    any
  >['components'];

  return (
    <ReactSelectAsyncInput
      {...otherProps}
      id={id}
      name={name}
      components={customComponents}
      placeholder={placeholder}
      defaultOptions={defaultOptions}
      noOptionsMessage={noOptionsMessage}
      loadOptions={makeFetchTerm({
        url: admin
          ? Routes.quick_search_backoffice_networks_path()
          : Routes.quick_search_users_path(),
        params: {
          sort_by: 'lastname',
          term: inputValue,
          per_page: 30,
          ...(searchType && { search_type: searchType }),
        },
      })}
    />
  );
};

export default ReactSelectUsers;
